import { supabase } from "../supabaseClient";

/**
 * User Profile Services
 */
export const fetchUserData = async (userId) => {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .maybeSingle();
  return { data, error };
};

export const updateUsername = async (userId, username) => {
  const { data, error } = await supabase
    .from('users')
    .update({ username })
    .eq('id', userId)
    .select();
  return { data, error };
};

export const resetDailyCounts = async (userId) => {
  const { error } = await supabase
    .from('users')
    .update({
      daily_games_count: 0,
      successful_games_count: 0
    })
    .eq('id', userId);
  return { error };
};

export const initUserRow = async (userData) => {
  const { data, error } = await supabase.from('users').upsert(userData).select();
  return { data, error };
};

export const updateUserStats = async (userId, updates) => {
  const { error } = await supabase
    .from('users')
    .update(updates)
    .eq('id', userId);
  return { error };
};

/**
 * Word Stats Services
 */
export const fetchWordStats = async (userId) => {
  const { data, error } = await supabase
    .from('word_stats')
    .select('*')
    .eq('user_id', userId);
  return { data, error };
};

export const updateWordStatsDB = async (userId, word, isCorrect) => {
  word = word.toLowerCase().trim();

  // Fetch existing stats to calculate relative increments
  const { data: existing, error: fetchError } = await supabase
    .from('word_stats')
    .select('*')
    .eq('user_id', userId)
    .eq('word', word)
    .maybeSingle();

  if (fetchError) return { error: fetchError };

  let newSeenCount = 1;
  let newCorrectCount = isCorrect ? 1 : 0;
  let newWrongCount = isCorrect ? 0 : 1;
  let newStreak = isCorrect ? 1 : -1;

  if (existing) {
    newSeenCount = (existing.seen_count || 0) + 1;
    newCorrectCount = isCorrect ? (existing.correct_count || 0) + 1 : (existing.correct_count || 0);
    newWrongCount = isCorrect ? (existing.wrong_count || 0) : (existing.wrong_count || 0) + 1;
    
    newStreak = existing.streak || 0;
    if (isCorrect) {
      newStreak = (existing.last_result === true) ? newStreak + 1 : 1;
    } else {
      newStreak = (existing.last_result === false) ? newStreak - 1 : -1;
    }
  }

  const payload = {
    user_id: userId,
    word,
    seen_count: newSeenCount,
    correct_count: newCorrectCount,
    wrong_count: newWrongCount,
    streak: newStreak,
    last_result: isCorrect,
    last_seen: new Date().toISOString()
  };

  if (existing && existing.id) {
    payload.id = existing.id;
  }

  // Use upsert to handle race conditions where a row might have been created
  // between the select and the write.
  const { error: upsertError } = await supabase
    .from('word_stats')
    .upsert([payload]);

  return { error: upsertError };
};

/**
 * Game Session Services
 */
export const saveGameSession = async (userId, sessionData) => {
  const { error } = await supabase
    .from('game_sessions')
    .insert([{
      user_id: userId,
      game_type: sessionData.gameType,
      mode: sessionData.mode,
      score: sessionData.score,
      accuracy: sessionData.accuracy,
      correct: sessionData.correct,
      wrong: sessionData.wrong,
      speedy: sessionData.speedy
    }]);
  return { error };
};

export const fetchGameSessions = async (userId, gameType, mode) => {
  const { data, error } = await supabase
    .from('game_sessions')
    .select('*')
    .eq('user_id', userId)
    .eq('game_type', gameType)
    .eq('mode', mode)
    .order('created_at', { ascending: false })
    .limit(5);
  return { data: (data || []).reverse(), error };
};

export const fetchBestScore = async (userId, gameType, mode) => {
  const { data, error } = await supabase
    .from('game_sessions')
    .select('score')
    .eq('user_id', userId)
    .eq('game_type', gameType)
    .eq('mode', mode)
    .order('score', { ascending: false })
    .limit(1)
    .maybeSingle();
  return { bestScore: data?.score || 0, error };
};
